import React, { useState, useEffect, useRef, useContext, useCallback } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Paper
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";


const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
  },
}));

const MessageEditSchema = Yup.object().shape({
  message: Yup.string()
    .min(1, "Too Short!")
    .max(30000, "Too Long!")
    .required("Required"),
});

const MessageEditModal = ({
  open,
  onClose,
  message
}) => {

  
  const classes = useStyles();
  const isMounted = useRef(true);
  const { user } = useContext(AuthContext);
  const [messageEdit, setMessageEdit] = useState({});
  const [file, setFile] = useState(null);
  const [existingMedia, setExistingMedia] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);

  useEffect(() => {

    setFile(null);
    setRemoveImage(false);
    setExistingMedia(message?.mediaUrl || null);
    setMessageEdit({message: message?.body})
    return () => {
      isMounted.current = false;
    };

  }, [message]);



  const handleClose = () => {
    onClose(false);
  };

  const handleEditMessage = async (values) => {
    try {
      const formData = new FormData();
      
      // Se for mensagem nova, formata o corpo com o nome do usuário
      let body = values.message;
      if (!message?.id) {
        body = `*${user?.name}:*\n${values.message.trim()}`;
      }

      // Adicionando campos ao FormData
      formData.append("id", message?.id || "");
      formData.append("ticketId", message?.ticketId);
      formData.append("body", body);
      formData.append("isInternal", message?.isInternal || false);
      formData.append("removeImage", removeImage);

      // Adicionando o arquivo (se existir)
      if (file) {
        formData.append("medias", file); // O backend geralmente espera 'medias'
      }

      await api.post(`/messages/edit`, formData, { timeout: 15000 });
      
      toast.success('Processado com Sucesso!');
      handleClose();
    } catch (err) {
      console.log('Error', err);
      toastError("Erro ao salvar a mensagem!");
    }
  };

  // Dentro do componente MessageEditModal, antes do handlePaste:
  const isInternal = message?.isInternal;

  const handlePaste = useCallback((e) => {
    // Agora usamos a variável simples
    if (!isInternal) return;

    const items = (e.clipboardData || e.originalEvent.clipboardData).items;
    for (const item of items) {
      if (item.kind === 'file') {
        const blob = item.getAsFile();
        setFile(blob);
      }
    }
  }, [isInternal]); // Dependência limpa e estática

  // 2. Registre o evento quando o modal abrir
  useEffect(() => {
    if (open) {
      window.addEventListener("paste", handlePaste);
    }
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [open, handlePaste]);

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-edit">
         { message?.id ? "Editar Mensagem" : "Enviar Mensagem"}
        </DialogTitle>
        <Formik
          initialValues={messageEdit}
          enableReinitialize={true}
          validationSchema={MessageEditSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleEditMessage(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label="Nova Mensagem"
                    name="message"
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    multiline
                    minRows={5}
                    fullWidth
                  />
                </div>
                {/* Apenas permite anexar se for nota interna */}
                {message?.isInternal && (
                  <div style={{ marginTop: 15, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                    
                    {/* 1. Se NÃO tem arquivo novo e NÃO tem mídia existente, mostra botão de anexo */}
                    {!file && !existingMedia ? (
                      <>
                        <input
                          type="file"
                          id="file-upload"
                          onChange={(e) => {
                            setFile(e.target.files[0]);
                            setExistingMedia(null); // Ao selecionar novo, remove o "fantasma" da antiga
                          }}
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="file-upload" style={{ width: '100%' }}>
                          <Button variant="outlined" component="span" fullWidth>
                            Anexar Arquivo (Nota Interna)
                          </Button>
                        </label>
                      </>
                    ) : (
                      <>
                        {/* 2. Mostra Preview: Prioridade para o novo arquivo (file), senão a mídia existente */}
                        {file ? (
                          // Preview do NOVO arquivo selecionado
                          file.type?.startsWith('image/') ? (
                            <img 
                              src={URL.createObjectURL(file)} 
                              alt="Novo Preview" 
                              style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '5px' }} 
                            />
                          ) : (
                            <Paper variant="outlined" style={{ padding: '10px', width: '100%', textAlign: 'center' }}>
                              <strong>NOVO ARQUIVO:</strong> {file.name}
                            </Paper>
                          )
                        ) : (
                          // Preview da mídia que JÁ ESTAVA na mensagem (do servidor)
                          message.mediaType === 'image' ? (
                            <img 
                              src={`${process.env.REACT_APP_BACKEND_URL}/public/${existingMedia}`} 
                              alt="Mídia Existente" 
                              style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '5px', opacity: 0.8 }} 
                            />
                          ) : (
                            <Paper variant="outlined" style={{ padding: '10px', width: '100%', textAlign: 'center' }}>
                              <strong>MÍDIA ATUAL:</strong> {existingMedia}
                            </Paper>
                          )
                        )}

                        {/* 3. Botão Remover (limpa ambos) */}
                        <Button 
                          size="small" 
                          color="secondary" 
                          onClick={() => {
                            setFile(null);
                            setExistingMedia(null);
                            setRemoveImage(true);
                            // Aqui você pode setar a flag setRemoveImage(true) se for implementar a exclusão
                          }}
                        >
                          REMOVER ANEXO
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  { message?.id ? "Editar" : "Enviar"}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default MessageEditModal;
